import React, { FormEvent } from "react";
import { Package, FileText, Calendar, Upload, DollarSign } from "lucide-react";
import { ValidationErrors } from "src/errors";
import {
  UpdatePurchaseOrderInvoiceFormData,
  UpdatePurchaseOrderInvoiceFormDataKey,
  PurchaseOrder,
  UpdatePurchaseOrderInvoiceFormAction
} from "src/types";
import { useScrollOnMessage } from "src/hooks";
import { SubmissionTooltip } from "src/components/tooltips";
import { DateInputBox, TextInputBox, TextInputBoxLarge, MultiFileUpload } from "src/components/ui/inputs";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";

interface UpdatePurchaseOrderInvoiceFormProps {
  formData: UpdatePurchaseOrderInvoiceFormData;
  purchaseOrderToUpdate?: PurchaseOrder;
  purchaseOrderOptions?: string[];
  validationErrors?: ValidationErrors<UpdatePurchaseOrderInvoiceFormDataKey>;
  formLvlErrorMessage: string;
  isFormUpdated: boolean;
  isFormComplete: boolean;
  handleInputChange: (
    field: UpdatePurchaseOrderInvoiceFormDataKey,
    value: UpdatePurchaseOrderInvoiceFormData[keyof UpdatePurchaseOrderInvoiceFormData]
  ) => void;
  handleSubmit: (e?: FormEvent) => Promise<void>;
  setAction: (action: UpdatePurchaseOrderInvoiceFormAction) => void;
}

const UpdatePurchaseOrderInvoiceForm: React.FC<UpdatePurchaseOrderInvoiceFormProps> = ({
  formData,
  purchaseOrderToUpdate,
  purchaseOrderOptions,
  validationErrors,
  formLvlErrorMessage,
  isFormUpdated,
  isFormComplete,
  handleInputChange,
  handleSubmit,
  setAction
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Update Invoice</h1>
            <p className="text-gray-300 mt-2 text-lg">Update invoice details and documentation</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-6">
              {/* Search Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <Package className="w-4 h-4 mr-2" />
                  Purchase Order Search
                </h3>
                <TextInputBox
                  id="purchase-order-id"
                  placeholder="Purchase Order ID"
                  onChange={(value) => {
                    handleInputChange("purchaseOrderId", value.toUpperCase());
                  }}
                  value={formData.purchaseOrderId}
                  dropDownOptions={purchaseOrderOptions}
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("purchaseOrderId")} />
              </div>

              {purchaseOrderToUpdate && (
                <>
                  {/* Invoice Upload Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <Upload className="w-4 h-4 mr-2" />
                      Invoice Upload
                    </h3>
                    <div className="mb-3">
                      <p className="text-sm text-secondary-600 italic">Please upload the VAT/official invoice</p>
                    </div>
                    <MultiFileUpload
                      uploadText="Upload Invoice PDF File(s)"
                      uploadedFiles={formData.invoiceFiles}
                      onFileChange={(files) => handleInputChange("invoiceFiles", files)}
                      fileFormat=".pdf,.xlsx"
                    />
                    <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("invoiceFiles")} />
                  </div>

                  {/* Invoice Reference Number Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <FileText className="w-4 h-4 mr-2" />
                      Invoice Reference Number
                    </h3>
                    <TextInputBox
                      id="invoice-reference-number"
                      placeholder="Invoice Reference Number (Optional)"
                      onChange={(value) => handleInputChange("invoiceReferenceNumber", value)}
                      value={formData.invoiceReferenceNumber}
                    />
                    <ValidationErrorMessage
                      message={validationErrors?.getErrorMessageByFormDataKey("invoiceReferenceNumber")}
                    />
                  </div>

                  {/* Invoice Due Date Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      Invoice Due Date
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <DateInputBox
                          id="invoice-due-date"
                          label="Invoice Due Date"
                          value={formData.invoiceDueDate}
                          onChange={(value) => handleInputChange("invoiceDueDate", value)}
                          disabled={purchaseOrderToUpdate.isProForma}
                        />
                        <ValidationErrorMessage
                          message={validationErrors?.getErrorMessageByFormDataKey("invoiceDueDate")}
                        />
                      </div>
                      {purchaseOrderToUpdate.isProForma && (
                        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <DollarSign className="w-5 h-5 text-blue-600" />
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="proforma-checkbox"
                              checked={true}
                              disabled={true}
                              className="rounded border-gray-300 cursor-not-allowed"
                            />
                            <label htmlFor="proforma-checkbox" className="text-sm font-medium text-blue-800">
                              Proforma Invoice (Due date is auto-set to today)
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Additional Information Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <FileText className="w-4 h-4 mr-2" />
                      Additional Information
                    </h3>
                    <TextInputBoxLarge
                      id="additional-info"
                      placeholder="Additional Information (Optional)"
                      onChange={(value) => handleInputChange("additionalInfo", value)}
                      value={formData.additionalInfo}
                      maxLength={1000}
                    />
                    <ValidationErrorMessage
                      message={validationErrors?.getErrorMessageByFormDataKey("additionalInfo")}
                    />
                  </div>
                </>
              )}

              {/* Form Level Error */}
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

              {/* Submit Button */}
              <div className="flex items-center gap-4">
                <button
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                  disabled={!isFormComplete || !isFormUpdated}
                  onClick={() => setAction(!purchaseOrderToUpdate ? "FETCH" : "UPDATE")}
                  type="submit"
                >
                  {!purchaseOrderToUpdate ? "Search" : "Update Invoice"}
                </button>

                {purchaseOrderToUpdate && (!isFormComplete || !isFormUpdated) && (
                  <SubmissionTooltip formState={isFormUpdated ? "INCOMPLETE" : "NO_CHANGE"} />
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { UpdatePurchaseOrderInvoiceForm };
